import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { Dispute } from "@/types";
import api from "@/services/api";

export interface DisputeFilter {
  status?: Dispute["status"];
  caseId?: string;
  searchTerm?: string;
}

export interface DisputeSort {
  field: keyof Dispute | null;
  direction: "asc" | "desc";
}

interface DisputesState {
  items: Dispute[];
  loading: boolean;
  error: string | null;
  filter: DisputeFilter;
  sort: DisputeSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: DisputesState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

export const fetchDisputes = createAsyncThunk(
  "disputes/fetchDisputes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get<any>("/v1/disputes");
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch disputes");
    }
  }
);

const disputesSlice = createSlice({
  name: "disputes",
  initialState,
  reducers: {
    setDisputeFilter: (state, action: PayloadAction<DisputeFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setDisputeSort: (state, action: PayloadAction<DisputeSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setDisputePage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setDisputePageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetDisputeFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addDispute: (state, action: PayloadAction<Dispute>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateDispute: (state, action: PayloadAction<Dispute>) => {
      const index = state.items.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteDispute: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((d) => d.id !== action.payload);
      state.total -= 1;
    },
    resetDisputes: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDisputes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDisputes.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.items = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        state.total = state.items.length;
        state.error = null;
      })
      .addCase(fetchDisputes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setDisputeFilter,
  setDisputeSort,
  setDisputePage,
  setDisputePageSize,
  resetDisputeFilters,
  addDispute,
  updateDispute,
  deleteDispute,
  resetDisputes,
} = disputesSlice.actions;
export default disputesSlice.reducer;

// Selectors
export const selectDisputes = (state: any) => state.disputes.items;
export const selectDisputesLoading = (state: any) => state.disputes.loading;
export const selectDisputesError = (state: any) => state.disputes.error;
export const selectDisputeFilter = (state: any) => state.disputes.filter;
export const selectDisputeSort = (state: any) => state.disputes.sort;
export const selectDisputePagination = (state: any) => ({
  currentPage: state.disputes.currentPage,
  pageSize: state.disputes.pageSize,
  total: state.disputes.total,
});

export const selectFilteredDisputes = createSelector(
  [(state: any) => state.disputes.items, (state: any) => state.disputes.filter, (state: any) => state.disputes.sort, (state: any) => state.disputes.currentPage, (state: any) => state.disputes.pageSize],
  (items, filter, sort, currentPage, pageSize) => {
    let filtered = [...items];

    if (filter.status) {
      filtered = filtered.filter((d) => d.status === filter.status);
    }
    if (filter.caseId) {
      filtered = filtered.filter((d) => d.caseId === filter.caseId);
    }
    if (filter.searchTerm) {
      const term = filter.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (d) =>
          d.id.toLowerCase().includes(term) ||
          d.reason?.toLowerCase().includes(term)
      );
    }

    if (sort.field) {
      filtered.sort((a, b) => {
        const aVal = a[sort.field!] || "";
        const bVal = b[sort.field!] || "";
        const comparison = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        return sort.direction === "asc" ? comparison : -comparison;
      });
    }

    const startIdx = (currentPage - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    const paginated = filtered.slice(startIdx, endIdx);

    return {
      data: paginated,
      total: filtered.length,
      currentPage,
      pageSize,
      hasNextPage: endIdx < filtered.length,
      hasPrevPage: currentPage > 1,
    };
  }
);
